/**
 * Utilidades para formatear nombres de materias
 * Evita duplicación de código entre componentes
 */

/**
 * Formatea el nombre de una materia aplicando mapeos y limpieza
 * @param {string} displayName - Nombre original de la materia
 * @returns {string} - Nombre formateado
 */
export function formatSubjectName(displayName) {
  if (!displayName) return '';
  
  // Aplicar mapeos de nombres ANTES de limpiar sufijos
  const nameMapping = {
    'Lenguaje': 'Competencia Lectora',
    'Matemática': 'Matemática (M1)',
    'Matemática Electivo': 'Matemática (M2) Electivo'
  };
  
  // Verificar si el nombre completo está en el mapeo
  if (nameMapping[displayName]) {
    return nameMapping[displayName];
  }
  
  // Casos especiales para "Matemática (M)" - detectar si es electivo o no
  if (displayName === 'Matemática (M)') {
    // Si no hay contexto de curso, devolver M1 por defecto
    return 'Matemática (M1)';
  }
  
  // Extraer el nombre base removiendo sufijos comunes
  let baseName = displayName
    .replace(/\s*(Tarde|Mañana|Noche)\s*\d+\s*/gi, '') // Remover "Tarde 403", "Mañana 402", etc.
    .replace(/\s*\(\d+\s*Sesiones\)\s*/gi, '') // Remover "(2 Sesiones)"
    .replace(/\s*\d+\s*/g, '') // Remover números sueltos
    .replace(/\s*MENCIÓN\s*/gi, '')
    .replace(/\s*ELECTIVO\s*/gi, '')
    .replace(/\s*COMÚN\s*/gi, '')
    .trim();
  
  // Casos especiales para Matemática
  if (baseName === 'MatemáticaTarde') {
    baseName = 'Matemática';
  }
  
  if (baseName.includes('Electivo') || baseName.includes('electivo')) {
    baseName = 'Matemática Electivo';
  }
  
  // Aplicar mapeos de nombres al nombre base limpio
  return nameMapping[baseName] || baseName;
}

/**
 * Obtiene el tipo de etiqueta para un curso
 * @param {Object} course - Objeto del curso
 * @returns {string|null} - Tipo de etiqueta o null
 */
export function getCourseLabelType(course) {
  if (!course) return null;
  
  if (course.hasMention) {
    return 'Mención';
  } else if (course.isElective) {
    return 'Electivo';
  } else if (course.isCommon) {
    return 'Común';
  }
  
  return null;
}

/**
 * Formatea el nombre completo de un curso incluyendo su etiqueta
 * @param {string} originalSubjectName - Nombre original de la materia
 * @param {Object} course - Objeto del curso
 * @returns {string} - Nombre completo formateado
 */
export function formatFullCourseName(originalSubjectName, course) {
  // Caso especial para Matemática: determinar si es M1 o M2 Electivo
  if (originalSubjectName && originalSubjectName.toLowerCase().includes('matemática')) {
    // Verificar si contiene "Electivo" en el nombre original o si el curso es electivo
    const containsElectivo = originalSubjectName.toLowerCase().includes('electivo') || 
                            (course && course.isElective);
    
    if (containsElectivo) {
      return 'Matemática (M2) Electivo';
    } else {
      return 'Matemática (M1)';
    }
  }
  
  const displayName = formatSubjectName(originalSubjectName);
  
  // Si el nombre original contiene COMÚN o Común, convertir a Común
  if (originalSubjectName && (/COMÚN/gi.test(originalSubjectName) || /Común/gi.test(originalSubjectName))) {
    return displayName + ' Común';
  }
  
  // Si el nombre original contiene MENCIÓN, convertir a Mención
  if (originalSubjectName && /MENCIÓN/gi.test(originalSubjectName)) {
    return displayName + ' Mención';
  }
  
  // Si el nombre original contiene ELECTIVO, convertir a Electivo
  if (originalSubjectName && /ELECTIVO/gi.test(originalSubjectName)) {
    return displayName + ' Electivo';
  }
  
  const labelType = getCourseLabelType(course);
  
  // Si no hay etiqueta que agregar, devolver el nombre tal como está
  if (!labelType) {
    return displayName;
  }
  
  // Verificar si el nombre ya contiene alguna etiqueta
  const hasExistingLabel = displayName.toLowerCase().includes('mención') || 
                          displayName.toLowerCase().includes('electivo') || 
                          displayName.toLowerCase().includes('común');
  
  // Si ya tiene una etiqueta, no agregar otra
  if (hasExistingLabel) {
    return displayName;
  }
  
  // Si no tiene etiqueta, agregar la correspondiente
  return `${displayName} ${labelType}`;
}
